import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface QualityCheckpoint {
  id: number;
  checkpoint: string;
  tests: string[];
  acceptance: string;
  frequency: string;
}

const checkpoints: QualityCheckpoint[] = [
{
  id: 1,
  checkpoint: 'Pre-Treatment Verification',
  tests: ['Water break test', 'Coating weight measurement', 'Surface pH verification'],
  acceptance: 'Complete water sheeting, 50-150 mg/ft² coating weight',
  frequency: 'Every batch'
},
{
  id: 2,
  checkpoint: 'Powder Application',
  tests: ['Film thickness gauge', 'Visual appearance', 'Coverage uniformity'],
  acceptance: '2-4 mils thickness, uniform coverage, no holidays',
  frequency: 'Continuous monitoring'
},
{
  id: 3,
  checkpoint: 'Cure Verification',
  tests: ['MEK double rub test', 'Pencil hardness', 'Gloss measurement'],
  acceptance: '>100 double rubs, ≥2H hardness, ±5 gloss units',
  frequency: 'Every production run'
},
{
  id: 4,
  checkpoint: 'Final Inspection',
  tests: ['Adhesion cross-hatch', 'Impact resistance', 'Salt spray readiness'],
  acceptance: '5B adhesion rating, no coating failure at 160 in-lbs',
  frequency: 'Per specification'
}];


const QualityCheckpoints = () => {
  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
              <Icon name="ClipboardDocumentCheckIcon" size={20} variant="outline" className="text-primary" />
              <span className="text-sm font-cta font-semibold text-primary">
                Systematic Verification
              </span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Quality Checkpoint System
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Multi-stage inspection protocol validates coating performance at every critical process step. Our systematic approach ensures consistent quality and long-term durability.
            </p>
            <div className="flex items-start space-x-3 p-4 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-success flex-shrink-0" />
              <div>
                <div className="text-sm font-cta font-semibold text-foreground mb-1">
                  ISO 9001:2015 Certified Process
                </div>
                <div className="text-sm text-muted-foreground">
                  All quality checkpoints documented and traceable per ISO requirements
                </div>
              </div>
            </div>
          </div>

          <div className="relative h-96 rounded-xl overflow-hidden shadow-lg">
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1dc6acb9d-1766488116709.png"
              alt="Quality inspector in white coat using digital thickness gauge to measure powder coating on metal part"
              className="w-full h-full object-cover" />

          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {checkpoints.map((checkpoint) =>
          <div
            key={checkpoint.id}
            className="bg-card rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-base border border-border">

              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-headline font-bold text-foreground">
                  {checkpoint.checkpoint}
                </h3>
                <div className="px-3 py-1 bg-secondary/10 rounded-full">
                  <span className="text-xs font-cta font-semibold text-secondary">
                    {checkpoint.frequency}
                  </span>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div>
                  <div className="text-xs font-cta font-semibold text-foreground uppercase tracking-wide mb-2">
                    Test Methods
                  </div>
                  <div className="space-y-1">
                    {checkpoint.tests.map((test, index) =>
                  <div key={index} className="flex items-start space-x-2">
                        <Icon name="BeakerIcon" size={16} variant="outline" className="text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">{test}</span>
                      </div>
                  )}
                  </div>
                </div>

                <div className="pt-3 border-t border-border">
                  <div className="text-xs font-cta font-semibold text-foreground uppercase tracking-wide mb-2">
                    Acceptance Criteria
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {checkpoint.acceptance}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-card rounded-lg p-6 shadow-md border border-border text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckBadgeIcon" size={32} variant="solid" className="text-success" />
            </div>
            <div className="text-3xl font-headline font-bold text-foreground mb-2">1000+</div>
            <div className="text-sm text-muted-foreground">Hours Salt Spray Testing</div>
          </div>

          <div className="bg-card rounded-lg p-6 shadow-md border border-border text-center">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="DocumentChartBarIcon" size={32} variant="solid" className="text-accent" />
            </div>
            <div className="text-3xl font-headline font-bold text-foreground mb-2">99.8%</div>
            <div className="text-sm text-muted-foreground">First-Pass Quality Rate</div>
          </div>

          <div className="bg-card rounded-lg p-6 shadow-md border border-border text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="ClockIcon" size={32} variant="solid" className="text-primary" />
            </div>
            <div className="text-3xl font-headline font-bold text-foreground mb-2">24-48h</div>
            <div className="text-sm text-muted-foreground">Typical Turnaround Time</div>
          </div>
        </div>
      </div>
    </section>);

};

export default QualityCheckpoints;